import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, timer, Subscription } from 'rxjs';
import { MJAuthBase } from '@memberjunction/ng-auth-services';
import { LogStatus, LogError } from '@memberjunction/core';
import { Router } from '@angular/router';
import { UserService } from './user.service';
import { PortalAccessLogService } from './portal-access-log.service';

export enum SessionStrategy {
  AUTO_REFRESH = 'auto_refresh',
  USER_PROMPT = 'user_prompt',
  NO_REFRESH = 'no_refresh'
}

export interface SessionConfig {
  strategy: SessionStrategy;
  warningMinutesBeforeExpiry: number;  // Minutes before expiry to show warning
  autoRefreshMinutesBeforeExpiry: number;  // Minutes before expiry to auto-refresh
  maxRefreshAttempts: number;
  checkIntervalSeconds: number;  // How often to check token expiry
}

export interface SessionWarning {
  show: boolean;
  expiresAt: Date;
  timeRemaining: string;
  canRefresh: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SessionManagementService implements OnDestroy {
  private sessionConfig: SessionConfig = {
    strategy: SessionStrategy.USER_PROMPT,  // Default to user prompt
    warningMinutesBeforeExpiry: 5,
    autoRefreshMinutesBeforeExpiry: 10,
    maxRefreshAttempts: 3,
    checkIntervalSeconds: 60  // Check every minute
  };

  private refreshAttempts = 0;
  private checkSubscription?: Subscription;
  private isRefreshing = false;

  // Observables for components to subscribe to
  private sessionWarningSubject = new BehaviorSubject<SessionWarning>({
    show: false,
    expiresAt: new Date(),
    timeRemaining: '',
    canRefresh: false
  });

  private sessionExpiredSubject = new BehaviorSubject<boolean>(false);

  public sessionWarning$ = this.sessionWarningSubject.asObservable();
  public sessionExpired$ = this.sessionExpiredSubject.asObservable();

  constructor(
    private authBase: MJAuthBase,
    private router: Router,
    private userService: UserService,
    private portalAccessLogService: PortalAccessLogService
  ) {
    this.startSessionMonitoring();
  }

  ngOnDestroy(): void {
    this.stopSessionMonitoring();
  }

  /**
   * Configure session management behavior
   */
  public configureSession(config: Partial<SessionConfig>): void {
    this.sessionConfig = { ...this.sessionConfig, ...config };
    LogStatus('Session management configured: ' + JSON.stringify(this.sessionConfig));
    
    // Restart monitoring with new config
    this.stopSessionMonitoring();
    this.startSessionMonitoring();
  }

  /**
   * Get current session configuration
   */
  public getSessionConfig(): SessionConfig {
    return { ...this.sessionConfig };
  }

  /**
   * Start monitoring token expiry
   */
  private startSessionMonitoring(): void {
    if (this.checkSubscription) {
      return; // Already monitoring
    }

    this.checkSubscription = timer(0, this.sessionConfig.checkIntervalSeconds * 1000)
      .subscribe(() => {
        this.checkTokenExpiry();
      });

    LogStatus('Session monitoring started');
  }

  /**
   * Stop monitoring token expiry
   */
  private stopSessionMonitoring(): void {
    if (this.checkSubscription) {
      this.checkSubscription.unsubscribe();
      this.checkSubscription = undefined;
    }
  }

  /**
   * Check if tokens are about to expire and take appropriate action
   */
  private checkTokenExpiry(): void {
    try {
      const tokenStorage = this.getOktaTokenStorage();
      if (!tokenStorage) {
        return; // No token storage
      }

      // Check for either idToken or accessToken
      let expiresAt: Date;
      if (tokenStorage.idToken && tokenStorage.idToken.expiresAt) {
        expiresAt = new Date(tokenStorage.idToken.expiresAt * 1000);
      } else if (tokenStorage.accessToken && tokenStorage.accessToken.expiresAt) {
        expiresAt = new Date(tokenStorage.accessToken.expiresAt * 1000);
      } else {
        LogStatus('No valid token found in storage');
        return;
      }

      const now = new Date();
      const minutesUntilExpiry = Math.floor((expiresAt.getTime() - now.getTime()) / (1000 * 60));

      LogStatus(`Token expires in ${minutesUntilExpiry} minutes at ${expiresAt.toISOString()}`);

      // Check if token has already expired
      if (minutesUntilExpiry <= 0) {
        LogStatus('Token has expired, handling expiry');
        this.handleTokenExpired();
        return;
      }

      // Handle based on strategy
      switch (this.sessionConfig.strategy) {
        case SessionStrategy.AUTO_REFRESH:
          this.handleAutoRefresh(minutesUntilExpiry, expiresAt);
          break;

        case SessionStrategy.USER_PROMPT:
          this.handleUserPrompt(minutesUntilExpiry, expiresAt);
          break;

        case SessionStrategy.NO_REFRESH:
          this.handleNoRefresh(minutesUntilExpiry, expiresAt);
          break;
      }

    } catch (error) {
      LogError('Error checking token expiry:', undefined, error);
    }
  }

  /**
   * Handle auto refresh strategy
   */
  private handleAutoRefresh(minutesUntilExpiry: number, expiresAt: Date): void {
    if (minutesUntilExpiry <= this.sessionConfig.autoRefreshMinutesBeforeExpiry && !this.isRefreshing) {
      LogStatus(`Auto-refreshing token (${minutesUntilExpiry} minutes until expiry)`);
      this.refreshTokens();
    }
  }

  /**
   * Handle user prompt strategy
   */
  private handleUserPrompt(minutesUntilExpiry: number, expiresAt: Date): void {
    const showWarning = minutesUntilExpiry <= this.sessionConfig.warningMinutesBeforeExpiry;
    const currentWarning = this.sessionWarningSubject.getValue();

    if (showWarning && !currentWarning.show) {
      const timeRemaining = this.formatTimeRemaining(minutesUntilExpiry);
      
      this.sessionWarningSubject.next({
        show: true,
        expiresAt,
        timeRemaining,
        canRefresh: this.refreshAttempts < this.sessionConfig.maxRefreshAttempts
      });

      LogStatus(`Session warning shown: ${timeRemaining} remaining`);
    } else if (showWarning && currentWarning.show) {
      // Update time remaining if warning is already showing
      const timeRemaining = this.formatTimeRemaining(minutesUntilExpiry);
      this.sessionWarningSubject.next({
        ...currentWarning,
        timeRemaining,
        expiresAt
      });
    }
  }

  /**
   * Handle no refresh strategy
   */
  private handleNoRefresh(minutesUntilExpiry: number, expiresAt: Date): void {
    // Just log approaching expiry
    if (minutesUntilExpiry <= this.sessionConfig.warningMinutesBeforeExpiry) {
      LogStatus(`Session will expire in ${minutesUntilExpiry} minutes`);
    }
  }

  /**
   * Handle token expiration
   */
  private handleTokenExpired(): void {
    LogStatus('Token has expired');

    // Calculate session duration and log expiry
    const sessionDuration = this.portalAccessLogService.getSessionDuration();
    const currentUser = this.userService.getUserInfo();

    if (currentUser) {
      this.portalAccessLogService.logSessionExpired(
        currentUser,
        sessionDuration
      ).catch(error => {
        LogError('Failed to log session expiry:', undefined, error);
      });
    }

    // Clear all Okta storage to prevent stale state issues
    // This ensures a fresh login attempt won't have PKCE mismatches
    // Using nuclear option (clear all okta-*) for maximum reliability
    try {
      LogStatus('Clearing all Okta storage (tokens, PKCE state, cache)...');

      // Remove all Okta localStorage items (nuclear option for broken state)
      const oktaKeys = Object.keys(localStorage).filter(key =>
        key.startsWith('okta-')
      );
      oktaKeys.forEach(key => {
        localStorage.removeItem(key);
        LogStatus(`  Removed: ${key}`);
      });

      // Clear session storage as well
      const oktaSessionKeys = Object.keys(sessionStorage).filter(key =>
        key.startsWith('okta-')
      );
      oktaSessionKeys.forEach(key => sessionStorage.removeItem(key));

      LogStatus(`✅ Cleared ${oktaKeys.length} localStorage keys and ${oktaSessionKeys.length} sessionStorage keys`);
    } catch (clearError) {
      LogError('⚠️ Error clearing expired token storage (non-critical):', undefined, clearError);
    }

    this.sessionExpiredSubject.next(true);
    this.hideSessionWarning();

    // Force logout to completely reset app state
    this.authBase.authenticated = false;

    // Trigger logout which will redirect to Okta login
    this.authBase.logout().catch(err => {
      LogError('Error during logout after token expiry:', undefined, err);
      // Fallback: reload page to force fresh auth
      window.location.reload();
    });
  }

  /**
   * Handle authentication required (refresh token expired)
   */
  private handleAuthenticationRequired(): void {
    LogStatus('Authentication required - refresh token expired or invalid');
    
    // Update session warning to show re-authentication message
    this.sessionWarningSubject.next({
      show: true,
      expiresAt: new Date(),
      timeRemaining: 'expired',
      canRefresh: false
    });

    // Reset attempts since we need full re-authentication
    this.refreshAttempts = 0;
  }

  /**
   * Refresh tokens using MJ Auth
   */
  public async refreshTokens(): Promise<boolean> {
    if (this.isRefreshing) {
      return false;
    }

    if (this.refreshAttempts >= this.sessionConfig.maxRefreshAttempts) {
      LogStatus('Max refresh attempts reached');
      this.handleTokenExpired();
      return false;
    }

    this.isRefreshing = true;
    this.refreshAttempts++;

    try {
      LogStatus(`Attempting token refresh (attempt ${this.refreshAttempts})`);
      LogStatus('Token storage before refresh:', this.getSessionInfo());
      
      const refresh$ = await this.authBase.refresh();
      const result = await refresh$.toPromise();
      
      LogStatus('Refresh result:', result);
      LogStatus('Token storage after refresh:', this.getSessionInfo());
      
      // The MJ Okta provider returns the userClaims$ observable
      // If we get here without throwing, the refresh was successful
      LogStatus('Token refresh successful');
      this.refreshAttempts = 0; // Reset attempts on success
      this.hideSessionWarning();
      this.isRefreshing = false;
      return true;
    } catch (error: any) {
      LogError(`Token refresh failed (attempt ${this.refreshAttempts}):`, undefined, error);
      this.isRefreshing = false;

      // Check if this is an authentication error that requires re-login
      const errorMessage = error?.message || '';
      const isAuthError = errorMessage.includes('not logged in') || 
                         errorMessage.includes('OAuthError') ||
                         errorMessage.includes('authentication') ||
                         errorMessage.includes('unauthorized');

      if (isAuthError) {
        LogStatus('Authentication error detected - user needs to re-login');
        this.handleAuthenticationRequired();
        return false;
      }

      if (this.refreshAttempts >= this.sessionConfig.maxRefreshAttempts) {
        this.handleTokenExpired();
      }
      
      return false;
    }
  }

  /**
   * User chose to extend session (from warning modal)
   */
  public async extendSession(): Promise<boolean> {
    const success = await this.refreshTokens();
    if (success) {
      this.hideSessionWarning();
    }
    return success;
  }

  /**
   * User chose to logout (from warning modal)
   */
  public logout(): void {
    this.hideSessionWarning();
    this.authBase.logout();
  }

  /**
   * Hide session warning
   */
  public hideSessionWarning(): void {
    this.sessionWarningSubject.next({
      show: false,
      expiresAt: new Date(),
      timeRemaining: '',
      canRefresh: false
    });
  }

  /**
   * Get Okta token storage from localStorage
   */
  private getOktaTokenStorage(): any {
    try {
      const storage = localStorage.getItem('okta-token-storage');
      return storage ? JSON.parse(storage) : null;
    } catch (error) {
      LogError('Error parsing okta-token-storage:', undefined, error);
      return null;
    }
  }

  /**
   * Format remaining time in human readable format
   */
  private formatTimeRemaining(minutes: number): string {
    if (minutes < 1) {
      return 'less than 1 minute';
    } else if (minutes === 1) {
      return '1 minute';
    } else if (minutes < 60) {
      return `${minutes} minutes`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return hours === 1 ? '1 hour' : `${hours} hours`;
      } else {
        return `${hours}h ${remainingMinutes}m`;
      }
    }
  }

  /**
   * Get current session info for debugging
   */
  public getSessionInfo(): any {
    const tokenStorage = this.getOktaTokenStorage();
    if (!tokenStorage) {
      return { status: 'No token storage found' };
    }

    let expiresAt: Date | null = null;
    let tokenType = 'none';

    if (tokenStorage.idToken && tokenStorage.idToken.expiresAt) {
      expiresAt = new Date(tokenStorage.idToken.expiresAt * 1000);
      tokenType = 'idToken';
    } else if (tokenStorage.accessToken && tokenStorage.accessToken.expiresAt) {
      expiresAt = new Date(tokenStorage.accessToken.expiresAt * 1000);
      tokenType = 'accessToken';
    }

    if (!expiresAt) {
      return { 
        status: 'No valid token found',
        tokenStorage: Object.keys(tokenStorage)
      };
    }

    const now = new Date();
    const minutesUntilExpiry = Math.floor((expiresAt.getTime() - now.getTime()) / (1000 * 60));

    return {
      tokenType,
      expiresAt: expiresAt.toISOString(),
      minutesUntilExpiry,
      refreshAttempts: this.refreshAttempts,
      strategy: this.sessionConfig.strategy,
      isRefreshing: this.isRefreshing,
      hasIdToken: !!tokenStorage.idToken,
      hasAccessToken: !!tokenStorage.accessToken
    };
  }
}